import { safeJsonObject } from '../lib/safeJson'
import type { ColumnInfo } from '../api/client'

interface StatusSettings {
  label_progress?: Record<string, number>
  label_progress_mode?: Record<string, string>
  done_colors?: unknown[]
}

interface LabelsRoot {
  labels?: Record<string, string>
}

/**
 * 列的 labels / settings 在前端类型里是对象，但早期代码里也有字符串形态；
 * 两种都接受（这里踩过一次坑：只认字符串导致全部算成空值）。
 */
function parseJson<T extends object>(raw: unknown): T | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'string' && typeof raw !== 'object') return null
  const v = safeJsonObject<T>(raw, {} as T, 'battery')
  return Object.keys(v).length === 0 ? null : v
}

/** 该 Status 列的「标签 idx → 百分比」；没有配置进度时返回 null */
function progressMap(statusColumn: ColumnInfo): Map<string, number> | null {
  const settings = parseJson<StatusSettings>(statusColumn.settings) ?? {}
  const progress = settings.label_progress ?? {}
  const modes = settings.label_progress_mode ?? {}
  const doneColors = new Set((settings.done_colors ?? []).map((x) => String(x)))
  // 与 LabelsEditor 的语义一致：三类配置任一非空即视为"设置了进度"
  const configured =
    Object.keys(progress).length > 0 || Object.keys(modes).length > 0 || doneColors.size > 0
  if (!configured) return null

  const labelsRoot = parseJson<LabelsRoot>(statusColumn.labels)
  const rawLabels =
    labelsRoot?.labels ?? (labelsRoot as unknown as Record<string, string> | null) ?? {}

  const out = new Map<string, number>()
  for (const idx of Object.keys(rawLabels)) {
    const p = typeof progress[idx] === 'number' ? progress[idx] : doneColors.has(idx) ? 100 : 0
    out.set(idx, Math.max(0, Math.min(100, p)))
  }
  if (out.size === 0) return null
  return out
}

/** 表格上所有「设置了进度」的 Status 列 */
function progressSources(columns: ColumnInfo[]): { key: string; pct: Map<string, number> }[] {
  const out: { key: string; pct: Map<string, number> }[] = []
  for (const c of columns) {
    if (c.type !== 'color') continue
    const map = progressMap(c)
    if (map !== null) out.push({ key: c.key, pct: map })
  }
  return out
}

/**
 * M9.5：进度列（columns-battery）的前端同步计算。
 *
 * 规则与服务端 `DerivedColumns.cpp` 的 columns-battery 分支**保持一致**：
 *   汇总表格上**所有设置了进度的 Status 列**，对本条目取平均值；
 *   没有值的 Status 列跳过；全部没值时给空。
 *
 * 为什么要前端再算一遍：服务端只在 GET 看板时算，改状态后不刷新就不会变；
 * 前端现算能让「改状态 → 进度立刻变」。
 */
/** 权重配置（M9.8）：默认关闭 = 等权平均 */
interface BatterySettings {
  weightsEnabled?: boolean
  sources?: { column?: string; weight?: number }[]
}

export function batteryValue(
  columns: ColumnInfo[],
  batteryColumn: ColumnInfo,
  values: Record<string, string>,
): string {
  const sources = progressSources(columns)
  if (sources.length === 0) return ''

  const cfg = parseJson<BatterySettings>(batteryColumn.settings) ?? {}
  const weightsEnabled = cfg.weightsEnabled === true
  const weightByKey = new Map<string, number>()
  for (const e of cfg.sources ?? []) {
    if (typeof e.column !== 'string') continue
    const w = typeof e.weight === 'number' && e.weight >= 0 ? e.weight : 1
    weightByKey.set(e.column, w)
  }

  let sum = 0
  let wsum = 0
  for (const src of sources) {
    const raw = (values[src.key] ?? '').trim()
    // 该列这一条没有值 → 整列跳过（与服务端一致）
    if (raw === '') continue
    // 有值但标签没配完成度 → 按 0 计（不管有没有权重）
    const pct = src.pct.get(raw) ?? 0
    const w = weightsEnabled ? (weightByKey.get(src.key) ?? 1) : 1
    sum += pct * w
    wsum += w
  }
  return wsum <= 0 ? '' : String(Math.round(sum / wsum))
}

/** 把条目 values 里的进度列值替换成前端现算的值（不修改原对象） */
export function withBatteryValues(
  columns: ColumnInfo[],
  values: Record<string, string>,
): Record<string, string> {
  const batteries = columns.filter((c) => c.type === 'columns-battery')
  if (batteries.length === 0) return values
  const next = { ...values }
  for (const col of batteries) next[col.key] = batteryValue(columns, col, values)
  return next
}